// Admin → Pages tab: per-page editable header (`page_header:{id}`),
// the Home hero carousel (`hero_slides:home`), and the multi-block content editor (`page_blocks:{id}`).
import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../api";
import { FileUploader } from "../../components/FileUploader";
import { PAGES_CATALOG, PAGE_CONTENT_DEFAULTS } from "../../components/PageContent";
import { BlocksEditor } from "./BlocksEditor";
import { HeroSlidesEditor } from "./HeroSlidesEditor";
import { MarqueeEditor } from "./MarqueeEditor";

export const PagesPanel = () => {
  const [selectedId, setSelectedId] = useState(PAGES_CATALOG[0].id);
  const selected = PAGES_CATALOG.find((p) => p.id === selectedId) || PAGES_CATALOG[0];

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-pages-panel">
      <aside className="lg:col-span-4">
        <div className="font-display text-2xl uppercase tracking-tight mb-3">Pages</div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mb-4">
          Every page header &mdash; fully editable. Saves are live the moment you click Save.
        </p>
        <div className="border-2 border-asphalt bg-bone shadow-hard divide-y-2 divide-asphalt" data-testid="admin-pages-list">
          {PAGES_CATALOG.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
              data-testid={`admin-pages-item-${p.id}`}
              className={`w-full text-left px-4 py-3 font-display uppercase tracking-tight transition-colors ${
                selectedId === p.id ? "bg-asphalt text-bone" : "hover:bg-card-light"
              }`}
            >
              <div className="text-base">{p.label}</div>
              <div className="font-mono text-[10px] tracking-[0.25em] opacity-70 mt-1">{p.path}</div>
            </button>
          ))}
        </div>
      </aside>
      <div className="lg:col-span-8 space-y-8" data-testid="admin-pages-editor">
        <PageContentForm key={`${selected.id}-content`} page={selected} />
        {selected.id === "home" && <MarqueeEditor key="marquee" />}
        {selected.id === "home" && <HeroSlidesEditor key={`${selected.id}-slides`} />}
        <BlocksEditor key={`${selected.id}-blocks`} page={selected} />
      </div>
    </div>
  );
};

const PageContentForm = ({ page }) => {
  const settingsKey = `page_header:${page.id}`;
  const [form, setForm] = useState({ ...PAGE_CONTENT_DEFAULTS, ...page.defaults });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(false);
  // Persistent visible status banner. Survives toast misses on mobile (toasts get clipped
  // behind iOS Safari's status bar). Stays on screen until the next save attempt.
  const [status, setStatus] = useState(null); // { kind: 'ok'|'err', message, at: Date }

  useEffect(() => {
    let active = true;
    api
      .get(`/settings/${encodeURIComponent(settingsKey)}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (v && typeof v === "object") {
          const merged = { ...PAGE_CONTENT_DEFAULTS, ...page.defaults, ...v };
          setForm(merged);
          setOriginal(merged);
        } else {
          setOriginal({ ...PAGE_CONTENT_DEFAULTS, ...page.defaults });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [settingsKey, page.defaults]);

  const dirty = original ? JSON.stringify(form) !== JSON.stringify(original) : true;

  const save = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.put(`/admin/settings/${encodeURIComponent(settingsKey)}`, { value: form });
      // Re-fetch from server to confirm the value actually persisted. This is the difference
      // between "the request returned 200" and "the new value is really live on this page".
      const verify = await api.get(`/settings/${encodeURIComponent(settingsKey)}`);
      const persisted = verify.data?.value;
      const ok = persisted && typeof persisted === "object" && persisted.title === form.title;
      setOriginal({ ...form });
      setStatus({
        kind: "ok",
        message: ok
          ? `Saved & verified — live at ${page.path}`
          : `Save returned 200 but the server still shows the old value. Try again or contact support.`,
        at: new Date(),
      });
      toast.success(`Saved — live at ${page.path}`);
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = formatError(err?.response?.data?.detail);
      const message =
        httpStatus === 401 || httpStatus === 403
          ? "Session expired — log out, log back in, then save again."
          : detail
          ? `Save failed (HTTP ${httpStatus || "?"}): ${detail}`
          : `Save failed: ${err?.message || "Network error"}. Check connection and retry.`;
      setStatus({ kind: "err", message, at: new Date() });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm(`Reset "${page.label}" to the original default copy?`)) return;
    setForm({ ...PAGE_CONTENT_DEFAULTS, ...page.defaults });
  };

  return (
    <form onSubmit={save} className="border-4 border-asphalt bg-card-light shadow-hard p-6 space-y-5" data-testid={`page-form-${page.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <SettingsIcon className="w-5 h-5" />
        <div className="font-display text-2xl uppercase tracking-tight">{page.label}</div>
        <a
          href={page.path}
          target="_blank"
          rel="noreferrer"
          data-testid={`page-form-${page.id}-preview`}
          className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-asphalt px-3 py-1.5 hover:bg-asphalt hover:text-bone"
        >
          &#8599; Open preview
        </a>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
        Live edit. Leave a field blank to use the default copy. Multi-line is supported &mdash; line breaks render on the page.
      </p>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Eyebrow (small uppercase line)</label>
        <input
          value={form.eyebrow}
          onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
          data-testid="page-field-eyebrow"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-mono"
          placeholder={page.defaults?.eyebrow || ""}
        />
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Title</label>
        <textarea
          rows={2}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="page-field-title"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-display text-xl resize-none"
          placeholder={page.defaults?.title || ""}
        />
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Subtitle (western-style tagline)</label>
        <textarea
          rows={2}
          value={form.subtitle}
          onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          data-testid="page-field-subtitle"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body resize-none"
          placeholder={page.defaults?.subtitle || ""}
        />
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Body (longer paragraph)</label>
        <textarea
          rows={5}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          data-testid="page-field-body"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body resize-none"
          placeholder={page.defaults?.body || ""}
        />
      </div>

      {page.id === "home" ? (
        <div className="border-2 border-terra bg-terra/10 p-4" data-testid="home-bg-notice">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra mb-1">
            ✦ Home hero is the Hero Carousel below
          </div>
          <p className="font-body text-sm text-ink leading-relaxed">
            The home page doesn&apos;t use a single background image here — its big top visual is the rotating{" "}
            <strong>Home Hero Carousel</strong> (scroll down on this page). Add or edit slides there. To change the
            site logo just for the home page, use the <strong>Page header logo</strong> field below.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          <FileUploader
            accept="image"
            label="Background image"
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            testid="page-field-image"
          />
          <FileUploader
            accept="video"
            label="Background video clip"
            value={form.video}
            onChange={(url) => setForm({ ...form, video: url })}
            testid="page-field-video"
          />
        </div>
      )}

      <div>
        <FileUploader
          accept="image"
          label="Page header logo (overrides the site default for this page only)"
          value={form.logo}
          onChange={(url) => setForm({ ...form, logo: url })}
          testid="page-field-logo"
        />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1 leading-relaxed">
          Optional. PNG / SVG / WebP — transparency preserved (uploaded logos skip the multiply blend).
          Leave blank to use the default Chibah mark.
        </p>
      </div>

      {page.id !== "home" && (
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.show_media}
              onChange={(e) => setForm({ ...form, show_media: e.target.checked })}
              data-testid="page-field-show-media"
            />
            Show media
          </label>
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.overlay}
              onChange={(e) => setForm({ ...form, overlay: e.target.checked })}
              data-testid="page-field-overlay"
            />
            Dark overlay for readability
          </label>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">CTA button label (optional)</label>
          <input
            value={form.cta_label}
            onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
            data-testid="page-field-cta-label"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-mono"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">CTA URL (optional)</label>
          <input
            value={form.cta_href}
            onChange={(e) => setForm({ ...form, cta_href: e.target.value })}
            data-testid="page-field-cta-href"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-mono"
            placeholder="/shop or https://..."
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-2 border-t-2 border-asphalt/30 items-start">
        <button
          type="submit"
          disabled={loading || !dirty}
          data-testid={`page-form-${page.id}-save`}
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : dirty ? "Save & publish" : "All saved"}
        </button>
        <button
          type="button"
          onClick={resetToDefaults}
          data-testid={`page-form-${page.id}-reset`}
          className="bg-bone text-asphalt border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-asphalt hover:text-bone"
        >
          Reset to default
        </button>
        {status && (
          <div
            className={`flex-1 min-w-[220px] border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] leading-snug ${
              status.kind === "ok"
                ? "border-asphalt bg-bone text-asphalt"
                : "border-terra bg-terra/10 text-terra"
            }`}
            data-testid={`page-form-${page.id}-status`}
          >
            <div className="text-[10px] opacity-70 mb-0.5">
              {status.at.toLocaleTimeString()} — {status.kind === "ok" ? "✓ saved" : "✗ failed"}
            </div>
            <div className="normal-case tracking-normal text-xs">{status.message}</div>
          </div>
        )}
      </div>
    </form>
  );
};
